import { z } from 'zod';
/**
 * Lists, the same everywhere (task 60): searched, filtered, sorted and paged on the server, so a table or a
 * set of cards shows one page of any number of rows. Every list endpoint takes these query parameters and
 * answers with a Page.
 *
 *   ?q=kinta&status=in_progress&sort=score&dir=asc&page=2&page_size=24
 */
/** Page sizes that fill a card grid of 2, 3 or 4 columns evenly. */
export const PAGE_SIZES = [12, 24, 48, 96];
export const DEFAULT_PAGE_SIZE = 24;
/**
 * The query schema of one list: search, the sorts it allows (the first is the default unless `sort` says
 * otherwise), its filters, and paging. Unknown parameters are refused, so a typo is a 400, not a silent
 * no-op.
 */
export function listQuery(spec) {
    return z.strictObject({
        q: z
            .string()
            .trim()
            .max(100)
            .optional()
            .transform((v) => v || undefined),
        sort: z.enum(spec.sorts).default((spec.sort ?? spec.sorts[0])),
        dir: z.enum(['asc', 'desc']).default(spec.dir ?? 'desc'),
        page: z.coerce.number().int().min(1).max(100_000).default(1),
        page_size: z.coerce
            .number()
            .int()
            .refine((n) => PAGE_SIZES.includes(n), `Use one of ${PAGE_SIZES.join(', ')}`)
            .default(DEFAULT_PAGE_SIZE),
        ...spec.filters,
    });
}
/** The query string for a list request: empty values left out. */
export function listSearch(params) {
    // Written out rather than URLSearchParams, which this package (no DOM, no Node types) can't name.
    const parts = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v)).replace(/%20/g, '+')}`);
    return parts.length ? `?${parts.join('&')}` : '';
}
/** "25–48 of 312" */
export function pageRange(p) {
    if (!p.total)
        return 'None';
    const from = (p.page - 1) * p.page_size + 1;
    const to = Math.min(p.total, p.page * p.page_size);
    return `${from}–${to} of ${p.total}`;
}
export const pageCount = (p) => Math.max(1, Math.ceil(p.total / p.page_size));
/** The page numbers to offer: first, last, and two either side of the current one, with gaps as null. */
export function pageNumbers(page, pages) {
    const want = new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2].filter((n) => n >= 1 && n <= pages));
    const sorted = [...want].sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
        if (i && n - sorted[i - 1] > 1)
            out.push(null);
        out.push(n);
    });
    return out;
}
//# sourceMappingURL=list.js.map